import { Application } from '@/types/application';
import { BackendApplication } from '@/types/backend';
import { Folder } from '@/types/folder';
import { sortBy } from './application';
import { isFolder, isNullOrUndefined } from './typeGuards';

export const getFolderLabels = (folderId: number, application: Application) => {
  let result = '';
  const currentFolder = application.folders?.find((folder) => folder.id === folderId);
  result += currentFolder?.label;
  if (currentFolder?.parentId) {
    result = getFolderLabels(currentFolder?.parentId, application) + '.' + result;
  }

  return result;
};

export const mergeFolders = (folders: Folder[]) => {
  const folderMap: { [key: number]: Folder } = {};
  const rootFolders: Folder[] = [];

  for (const folder of folders) {
    folder.folders = [];
    folder.applications = [];

    folderMap[folder.folderId] = folder;

    if (isNullOrUndefined(folder.parentFolderId)) {
      rootFolders.push(folder);
    }
  }

  for (const folder of folders) {
    if (folder.parentFolderId) {
      const parentFolder = folderMap[folder.parentFolderId];
      if (parentFolder) {
        parentFolder.folders?.push(folder);
      } else {
        console.warn(`Folder with id ${folder.folderId} has an invalid parentFolderId ${folder.parentFolderId}`);
      }
    }
  }

  return { folderMap, rootFolders };
};

export const mergeFoldersAndApplications = (folders: Folder[], applications: BackendApplication[]) => {
  const { rootFolders, folderMap } = mergeFolders(folders);
  for (const app of applications) {
    const folder = app.folderId && folderMap[app.folderId];
    if (folder) {
      folder.applications?.push(app);
    } else {
      console.warn(`Application with folderId ${app.folderId} doesn't match any folder.`);
    }
  }
  return rootFolders;
};

export const flattenFolderAppTreeToIds = (rootFolders: Folder[]) => {
  const result: string[] = [];
  const stack: (Folder | BackendApplication)[] = [...rootFolders]; // Starting with the top-level folders

  while (stack.length > 0) {
    const current = stack.pop();
    if (current) {
      if ('applicationId' in current) {
        result.push(`${(current as BackendApplication).applicationId}`);
      } else if (isFolder(current)) {
        result.push(`${current.folderId}`);
        if (current.applications) stack.push(...current.applications);
        if (current.folders) stack.push(...current.folders);
      }
    }
  }
  return result;
};

export const findApplicationPath = (rootFolders: Folder[] | undefined, applicationId: string): string | undefined => {
  if (!rootFolders) return;
  const traverse = (folders: Folder[], currentPath: string): string | undefined => {
    for (const folder of folders) {
      const newPath = currentPath ? `${currentPath}/${folder.name}` : `${folder.name}`;

      if (folder.applications && folder.applications.some((app) => app.applicationId === applicationId)) {
        return newPath; // Application found in current folder
      }

      if (folder.folders) {
        const subPath = traverse(folder.folders, newPath);
        if (subPath) {
          return subPath; // Application found in a sub-folder
        }
      }
    }

    return; // Application not found in the current branch
  };

  return traverse(rootFolders, '');
};

// Folders without matches are only dropped while a search is active, so empty folders stay visible otherwise.
export const filterApplicationsInFolders = (
  folders: Folder[],
  searchQuery: string,
  sortAppsBy: string,
  invert?: boolean
): Folder[] => {
  return sortBy('name', folders, invert)
    .map((folder) => filterApplications(folder, searchQuery, sortAppsBy, invert))
    .filter(
      (folder) =>
        !searchQuery ||
        (folder.applications && folder.applications.length > 0) ||
        (folder.folders && folder.folders.length > 0)
    );
};

export const filterApplications = (
  folder: Folder,
  searchQuery: string,
  sortAppsBy: string,
  invert?: boolean
): Folder => {
  const filteredApplications = folder.applications
    ? sortBy(sortAppsBy, folder.applications, invert)?.filter(
        (app) =>
          app.name?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase()) ||
          app.label?.toLocaleLowerCase().includes(searchQuery.toLocaleLowerCase())
      )
    : [];

  const filteredFolders = folder.folders
    ? sortBy('name', folder.folders, invert)
        ?.map((subfolder) => filterApplications(subfolder, searchQuery, sortAppsBy, invert))
        .filter(
          (subfolder) =>
            !searchQuery ||
            (subfolder.applications && subfolder.applications.length > 0) ||
            (subfolder.folders && subfolder.folders.length > 0)
        )
    : [];

  return {
    ...folder,
    applications: filteredApplications,
    folders: filteredFolders
  };
};
